import { useState, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetAllUserMarginsQuery,
  useLazySearchUserMarginsQuery,
  useGetUserMarginsQuery,
  useUpdateUserMarginMutation,
  useGetMarginTiersQuery,
  useUpdateMarginTierMutation,
  useAssignTierToUserMutation,
  useRemoveUserTierMutation,
  UserMarginSearchResult,
  UserMarginsData,
  GetAllUserMarginsItem,
  MarginTierData,
} from '../../store/api/adminApi';

type MarginType = 'temporaryNumbers' | 'proxies' | 'permanentNumbers' | 'voice' | 'temporaryNumbersReuse';

type FilterTab = 'all' | 'temporaryNumbers' | 'permanentNumbers' | 'proxies' | 'voice' | 'bronze' | 'silver' | 'gold' | 'premium';

const TIERS: { key: 'bronze' | 'silver' | 'gold' | 'premium'; label: string; color: string }[] = [
  { key: 'bronze', label: 'برونزي', color: 'text-amber-600' },
  { key: 'silver', label: 'فضي', color: 'text-slate-300' },
  { key: 'gold', label: 'غولدن', color: 'text-yellow-400' },
  { key: 'premium', label: 'بريميوم', color: 'text-purple-400' },
];

const SERVICE_TABS: { type: MarginType; label: string; servers: number[] }[] = [
  { type: 'temporaryNumbers', label: 'مؤقتة', servers: [1, 4, 5, 6] },
  { type: 'temporaryNumbersReuse', label: 'إعادة استخدام', servers: [5, 6] },
  { type: 'voice', label: 'اتصال', servers: [1] },
  { type: 'permanentNumbers', label: 'دائمة', servers: [1] },
  { type: 'proxies', label: 'بروكسيات', servers: [1, 2, 3] },
];

const SERVER_LABELS: Record<string, Record<number, string>> = {
  temporaryNumbers: { 1: 'SMSPool', 4: 'DurianRCS', 5: 'TextVerified', 6: 'PVA Deals' },
  temporaryNumbersReuse: { 5: 'TextVerified', 6: 'PVA Deals' },
  voice: { 1: 'TextVerified' },
  permanentNumbers: { 1: 'TextVerified' },
  proxies: { 1: 'ProxyCheap', 2: 'ProxyLine', 3: 'ProxyLTE' },
};

function formatMargin(m: number | null | undefined): string {
  if (m === null || m === undefined) return '—';
  return `${(m * 100).toFixed(2)}%`;
}

function getServerMargin(margins: UserMarginsData | null, type: MarginType, serverId: number): number | null {
  if (!margins) return null;
  if (type === 'temporaryNumbers') return margins.temporaryNumbers[serverId as 1 | 4 | 5 | 6] ?? null;
  if (type === 'temporaryNumbersReuse') return margins.temporaryNumbersReuse[serverId as 5 | 6] ?? null;
  if (type === 'permanentNumbers') return margins.permanentNumbers[serverId as 1] ?? null;
  if (type === 'voice') return margins.voice[serverId as 1] ?? null;
  return margins.proxies[serverId as 1 | 2 | 3] ?? null;
}

function hasAnyMargin(m: GetAllUserMarginsItem): boolean {
  if (!m.userId) return false;
  const t = m.temporaryNumbers;
  const p = m.permanentNumbers;
  const x = m.proxies;
  const v = m.voice;
  const r = m.temporaryNumbersReuse;
  return !!(t?.[1] ?? t?.[4] ?? t?.[5] ?? t?.[6] ?? p?.[1] ?? x?.[1] ?? x?.[2] ?? x?.[3] ?? v?.[1] ?? r?.[5]);
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function AdminUserMarginsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [modalUserId, setModalUserId] = useState<string | null>(null);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [serviceTab, setServiceTab] = useState<MarginType>('temporaryNumbers');
  const [editingCell, setEditingCell] = useState<{ type: MarginType; serverId: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [resetConfirm, setResetConfirm] = useState<{ type: MarginType; serverId: number } | null>(null);
  const [tierConfirm, setTierConfirm] = useState<{ userId: string; tier: 'bronze' | 'silver' | 'gold' | 'premium'; label: string } | null>(null);
  const [marginsPage, setMarginsPage] = useState(1);

  // Tier config state
  const [showTierConfig, setShowTierConfig] = useState(false);
  const [activeTierTab, setActiveTierTab] = useState<'bronze' | 'silver' | 'gold' | 'premium'>('bronze');
  const [tierConfigDirty, setTierConfigDirty] = useState(false);
  const [tierForm, setTierForm] = useState<Record<string, Record<number, string>>>({});

  // ---------- data ----------
  const { data: allMarginsData, isLoading: isAllLoading } = useGetAllUserMarginsQuery();
  const [triggerSearch, { data: searchData, isFetching: isSearching }] = useLazySearchUserMarginsQuery();
  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data: tiersData } = useGetMarginTiersQuery();
  const [updateMarginTier, { isLoading: isSavingTier }] = useUpdateMarginTierMutation();
  const [assignTierToUser, { isLoading: isAssigningTier }] = useAssignTierToUserMutation();
  const [removeUserTier] = useRemoveUserTierMutation();

  const tiers: MarginTierData[] = tiersData?.data?.tiers ?? [];

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!q) return;
    triggerSearch(q).catch(() => {});
  }, [debouncedSearch, triggerSearch]);

  const activeUserId = modalUserId ?? selectedUserId;
  const {
    data: userMarginsData,
    isLoading: isMarginsLoading,
    isFetching: isMarginsFetching,
  } = useGetUserMarginsQuery(activeUserId!, { skip: !activeUserId });

  const [updateUserMargin, { isLoading: isUpdating }] = useUpdateUserMarginMutation();

  // Initialize tier form when tiers data loads
  useEffect(() => {
    if (tiers.length > 0 && !tierConfigDirty) {
      const form: Record<string, Record<number, string>> = {};
      for (const tier of tiers) {
        form[tier.name] = {};
        const allServers = [
          ...Object.entries(tier.temporaryNumbers || {}).map(([k, v]) => ({ section: 'temporaryNumbers', sid: Number(k), val: v })),
          ...Object.entries(tier.proxies || {}).map(([k, v]) => ({ section: 'proxies', sid: Number(k), val: v })),
          ...Object.entries(tier.permanentNumbers || {}).map(([k, v]) => ({ section: 'permanentNumbers', sid: Number(k), val: v })),
          ...Object.entries(tier.voice || {}).map(([k, v]) => ({ section: 'voice', sid: Number(k), val: v })),
          ...Object.entries(tier.temporaryNumbersReuse || {}).map(([k, v]) => ({ section: 'temporaryNumbersReuse', sid: Number(k), val: v })),
        ];
        for (const entry of allServers) {
          form[tier.name][`${entry.section}:${entry.sid}`] = entry.val !== null ? String(entry.val * 100) : '';
        }
      }
      setTierForm(form);
    }
  }, [tiers, tierConfigDirty]);

  // ---------- derived ----------
  const currentMargins: UserMarginsData | null = userMarginsData?.data.margins ?? null;
  const selectedUser: UserMarginSearchResult | null = userMarginsData?.data.user ?? null;
  const isLoading = isMarginsLoading || isMarginsFetching;

  const allMargins: GetAllUserMarginsItem[] = useMemo(
    () => (allMarginsData?.data?.margins ?? []).filter(hasAnyMargin),
    [allMarginsData],
  );

  const filteredMargins = useMemo(() => {
    if (filterTab === 'all') return allMargins;
    if (filterTab === 'bronze' || filterTab === 'silver' || filterTab === 'gold' || filterTab === 'premium') {
      return allMargins.filter((item) => item.assignedTier === filterTab);
    }
    return allMargins.filter((item) => {
      if (filterTab === 'temporaryNumbers') return !!(item.temporaryNumbers[1] ?? item.temporaryNumbers[4] ?? item.temporaryNumbers[5] ?? item.temporaryNumbers[6]);
      if (filterTab === 'permanentNumbers') return !!item.permanentNumbers[1];
      if (filterTab === 'proxies') return !!(item.proxies[1] ?? item.proxies[2] ?? item.proxies[3]);
      if (filterTab === 'voice') return !!item.voice?.[1];
      return false;
    });
  }, [allMargins, filterTab]);

  const searchResults = searchData?.data?.users ?? null;

  const perPage = 6;
  const totalPages = Math.max(1, Math.ceil(filteredMargins.length / perPage));
  const safePage = Math.min(marginsPage, totalPages);
  const paginatedMargins = filteredMargins.slice((safePage - 1) * perPage, safePage * perPage);

  const stats = useMemo(() => {
    const temp = allMargins.filter((m) => m.temporaryNumbers[1] ?? m.temporaryNumbers[4] ?? m.temporaryNumbers[5] ?? m.temporaryNumbers[6]).length;
    const reuse = allMargins.filter((m) => m.temporaryNumbersReuse?.[5] != null || m.temporaryNumbersReuse?.[6] != null).length;
    const perm = allMargins.filter((m) => m.permanentNumbers[1]).length;
    const proxy = allMargins.filter((m) => m.proxies[1] ?? m.proxies[2] ?? m.proxies[3]).length;
    const voice = allMargins.filter((m) => m.voice?.[1]).length;
    return { total: allMargins.length, temp, reuse, perm, proxy, voice };
  }, [allMargins]);

  useEffect(() => { setMarginsPage(1); }, [filterTab]);

  // ---------- handlers ----------
  const handleSelectUser = useCallback((userId: string) => {
    setSelectedUserId(userId);
    setModalUserId(null);
    setEditingCell(null);
  }, []);

  const handleQuickView = useCallback((userId: string) => {
    setSelectedUserId(userId);
    setModalUserId(userId);
    setEditingCell(null);
    setServiceTab('temporaryNumbers');
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalUserId(null);
    setSelectedUserId(null);
    setEditingCell(null);
  }, []);

  const handleStartEdit = useCallback((type: MarginType, serverId: number, current: number | null) => {
    setEditingCell({ type, serverId });
    setEditValue(current !== null ? String(current * 100) : '');
  }, []);

  const handleSaveMargin = useCallback(async () => {
    if (!editingCell || !activeUserId) return;
    let marginValue: number | null;
    const trimmed = editValue.trim();
    if (trimmed === '' || trimmed === '0') {
      marginValue = null;
    } else {
      const parsed = parseFloat(trimmed);
      if (isNaN(parsed)) {
        toast.error('الرجاء إدخال رقم صحيح');
        return;
      }
      marginValue = parsed;
    }
    try {
      await updateUserMargin({
        userId: activeUserId,
        data: { type: editingCell.type, serverId: editingCell.serverId, margin: marginValue },
      }).unwrap();
      toast.success(marginValue !== null ? `تم تعيين الهامش إلى ${marginValue}%` : 'تم إزالة الهامش');
      setEditingCell(null);
    } catch {
      toast.error('فشل تحديث الهامش');
    }
  }, [editingCell, activeUserId, editValue, updateUserMargin]);

  const handleResetMargin = useCallback(async () => {
    if (!resetConfirm || !activeUserId) return;
    try {
      await updateUserMargin({
        userId: activeUserId,
        data: { type: resetConfirm.type, serverId: resetConfirm.serverId, margin: null },
      }).unwrap();
      toast.success('تم إزالة الهامش، سيعود المستخدم للهامش العام');
      setResetConfirm(null);
    } catch {
      toast.error('فشل إعادة تعيين الهامش');
    }
  }, [resetConfirm, activeUserId, updateUserMargin]);

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) { toast.error('الرجاء إدخال كلمة بحث'); return; }
    triggerSearch(q).catch(() => toast.error('فشل البحث'));
  }, [searchQuery, triggerSearch]);

  // ---- Tier config handlers ----
  const handleTierFormChange = useCallback((key: string, value: string) => {
    setTierForm((prev) => ({
      ...prev,
      [activeTierTab]: { ...prev[activeTierTab], [key]: value },
    }));
    setTierConfigDirty(true);
  }, [activeTierTab]);

  const handleSaveTierConfig = useCallback(async () => {
    const data: Record<string, Record<number, number | null>> = { temporaryNumbers: {}, proxies: {}, permanentNumbers: {}, voice: {}, temporaryNumbersReuse: {} };
    const entries = tierForm[activeTierTab] || {};
    for (const [key, val] of Object.entries(entries)) {
      const [section, sidStr] = key.split(':');
      const sid = Number(sidStr);
      const trimmed = val.trim();
      if (trimmed === '') {
        data[section][sid] = null;
      } else {
        const parsed = parseFloat(trimmed);
        if (isNaN(parsed)) {
          toast.error(`قيمة غير صالحة للسيرفر ${sid}`);
          return;
        }
        data[section][sid] = parsed / 100;
      }
    }
    try {
      await updateMarginTier({ tierName: activeTierTab, data }).unwrap();
      toast.success(`تم حفظ المستوى ${TIERS.find((t) => t.key === activeTierTab)?.label}`);
      setTierConfigDirty(false);
    } catch {
      toast.error('فشل حفظ المستوى');
    }
  }, [activeTierTab, tierForm, updateMarginTier]);

  const handleConfirmAssignTier = useCallback(async () => {
    if (!tierConfirm) return;
    try {
      await assignTierToUser({ userId: tierConfirm.userId, tier: tierConfirm.tier }).unwrap();
      toast.success(`تم تعيين المستوى ${tierConfirm.label} للمستخدم`);
      setSelectedUserId(tierConfirm.userId);
      setModalUserId(null);
      setTierConfirm(null);
    } catch {
      toast.error('فشل تعيين المستوى');
    }
  }, [tierConfirm, assignTierToUser]);

  const handleAssignTier = useCallback((userId: string, tier: 'bronze' | 'silver' | 'gold' | 'premium') => {
    const label = TIERS.find((t) => t.key === tier)?.label || '';
    setTierConfirm({ userId, tier, label });
  }, []);

  const handleRemoveTier = useCallback(async (userId: string) => {
    try {
      await removeUserTier(userId).unwrap();
      toast.success('تم إزالة تعيين المستوى');
    } catch {
      toast.error('فشل إزالة المستوى');
    }
  }, [removeUserTier]);

  // ---------- render: tier config modal ----------
  function renderTierConfig() {
    return (
      <div className="space-y-6">
        <p className="text-neutral-medium font-body text-sm">
          حدد نسبة الهامش لكل سيرفر في كل مستوى. القيم الفارغة تعني استخدام الهامش العام.
        </p>
        <div className="flex gap-2 mb-4 p-1 bg-neutral-dark/50 rounded-lg">
          {TIERS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setActiveTierTab(t.key); setTierConfigDirty(false); }}
              className={`flex-1 py-2.5 px-3 text-sm font-heading font-semibold rounded-md transition-all ${
                activeTierTab === t.key
                  ? 'bg-neutral-dark shadow-sm text-neutral-light'
                  : 'text-neutral-medium hover:text-neutral-light'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {SERVICE_TABS.map((tab) => (
          <div key={tab.type} className="space-y-3">
            <h4 className="text-sm font-heading font-semibold text-brand-accent">{tab.label}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tab.servers.map((sid) => {
                const key = `${tab.type}:${sid}`;
                const val = tierForm[activeTierTab]?.[key] ?? '';
                return (
                  <div key={key} className="flex items-center gap-2 bg-neutral-dark/40 rounded-lg p-3">
                    <label className="text-sm text-neutral-light font-body min-w-[90px]">{SERVER_LABELS[tab.type]?.[sid] ?? `سيرفر ${sid}`}</label>
                    <input
                      type="number"
                      value={val}
                      onChange={(e) => handleTierFormChange(key, e.target.value)}
                      placeholder="الهامش العام"
                      className="flex-1 px-3 py-2 bg-neutral-dark border border-neutral-600/60 rounded-lg text-neutral-light text-sm focus:outline-none focus:border-brand-accent font-body"
                      step="0.01"
                    />
                    <span className="text-xs text-neutral-medium font-body">%</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div className="flex gap-3 pt-4 border-t border-neutral-dark/50">
          <button
            onClick={() => { void handleSaveTierConfig(); }}
            disabled={isSavingTier}
            className="px-6 py-2.5 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {isSavingTier ? <ButtonSpinner /> : null}
            حفظ المستوى
          </button>
          <button
            onClick={() => setShowTierConfig(false)}
            className="px-6 py-2.5 text-neutral-medium font-body rounded-lg hover:text-neutral-light transition-colors"
          >
            إلغاء
          </button>
        </div>
      </div>
    );
  }

  // ---------- render: user row in list ----------
  function renderUserRow(item: GetAllUserMarginsItem) {
    const u = item.userId;
    if (!u) return null;
    const summary: { type: string; count: number }[] = [];
    let t = 0;
    if (item.temporaryNumbers[1] != null) t++;
    if (item.temporaryNumbers[4] != null) t++;
    if (item.temporaryNumbers[5] != null) t++;
    if (item.temporaryNumbers[6] != null) t++;
    if (t > 0) summary.push({ type: 'temporaryNumbers', count: t });
    if (item.temporaryNumbersReuse?.[5] != null || item.temporaryNumbersReuse?.[6] != null) summary.push({ type: 'temporaryNumbersReuse', count: 1 });
    if (item.permanentNumbers[1] != null) summary.push({ type: 'permanentNumbers', count: 1 });
    if (item.voice?.[1] != null) summary.push({ type: 'voice', count: 1 });
    let x = 0;
    if (item.proxies[1] != null) x++;
    if (item.proxies[2] != null) x++;
    if (item.proxies[3] != null) x++;
    if (x > 0) summary.push({ type: 'proxies', count: x });

    const tierInfo = TIERS.find((ti) => ti.key === item.assignedTier);

    return (
      <button
        key={item._id}
        onClick={() => handleQuickView(u._id)}
        className="group bg-gradient-to-br from-neutral-dark/50 to-neutral-dark/30 rounded-xl p-4 border-2 border-neutral-600/60 hover:border-brand-accent/50 shadow-sm shadow-black/5 hover:shadow-lg hover:shadow-brand-accent/10 transition-all duration-300 text-right"
      >
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-brand-accent/30 to-brand-accent/10 rounded-full flex items-center justify-center text-sm font-heading font-bold text-brand-accent">
              {(u.firstName?.[0] || u.email?.[0] || '?').toUpperCase()}
            </div>
            {summary.length > 0 && (
              <span className="absolute -top-1 -left-1 w-4 h-4 bg-green-500 border-2 border-brand-primary rounded-full flex items-center justify-center text-[8px] font-bold text-white">
                {summary.reduce((a, b) => a + b.count, 0)}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-semibold text-sm text-neutral-light group-hover:text-brand-accent transition-colors truncate">
              {u.firstName} {u.lastName || ''}
            </p>
            <p className="text-xs text-neutral-medium font-body truncate">
              {u.email && <span>{u.email}</span>}
              {u.telegramId && <span> • ID: {u.telegramId}</span>}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {tierInfo && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-heading font-semibold ${tierInfo.color} bg-white/5`}>
                {tierInfo.label}
              </span>
            )}
            <svg className="w-4 h-4 text-neutral-medium/40 group-hover:text-brand-accent transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
        {summary.length > 0 && (
          <div className="flex gap-1.5 mt-2 mr-[52px]">
              {summary.map((s) => {
              const col = s.type === 'temporaryNumbers' ? 'bg-blue-500/20 text-blue-400'
                : s.type === 'temporaryNumbersReuse' ? 'bg-cyan-500/20 text-cyan-400'
                : s.type === 'permanentNumbers' ? 'bg-purple-500/20 text-purple-400'
                : s.type === 'voice' ? 'bg-green-500/20 text-green-400'
                : 'bg-orange-500/20 text-orange-400';
              const letter = s.type === 'temporaryNumbers' ? 'T' : s.type === 'temporaryNumbersReuse' ? 'R' : s.type === 'permanentNumbers' ? 'P' : s.type === 'voice' ? 'C' : 'X';
              return (
                <span key={s.type} className={`text-[10px] px-1.5 py-0.5 rounded font-heading font-semibold ${col}`}>
                  {letter}{s.count > 1 ? s.count : ''}
                </span>
              );
            })}
          </div>
        )}
      </button>
    );
  }

  // ---------- render: modal detail ----------
  function renderModalContent() {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-brand-accent/30 border-t-brand-accent rounded-full animate-spin mb-4" />
          <p className="text-neutral-medium font-body">جاري التحميل...</p>
        </div>
      );
    }
    if (!selectedUser) return null;

    const margins = currentMargins ?? {
      assignedTier: null,
      temporaryNumbers: { 1: null, 4: null, 5: null, 6: null },
      temporaryNumbersReuse: { 5: null, 6: null },
      proxies: { 1: null, 2: null, 3: null },
      permanentNumbers: { 1: null },
      voice: { 1: null },
    };
    const currentTier = margins.assignedTier;
    const tierInfo = TIERS.find((t) => t.key === currentTier);

    return (
      <>
        {/* profile header */}
        <div className="flex items-center gap-4 mb-6 pb-5 border-b border-neutral-dark/50">
          <div className="w-12 h-12 bg-gradient-to-br from-brand-accent/30 to-brand-accent/10 rounded-2xl flex items-center justify-center text-lg font-heading font-bold text-brand-accent shrink-0">
            {(selectedUser.firstName?.[0] || selectedUser.email?.[0] || '?').toUpperCase()}
          </div>
          <div className="text-right flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-heading font-bold text-brand-accent truncate">
                {selectedUser.firstName} {selectedUser.lastName || ''}
              </h2>
              {tierInfo && (
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-heading font-semibold ${tierInfo.color} bg-white/5`}>
                  {tierInfo.label}
                </span>
              )}
            </div>
            <div className="text-sm text-neutral-medium font-body flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
              {selectedUser.email && <span>{selectedUser.email}</span>}
              {selectedUser.telegramId && <span>ID: {selectedUser.telegramId}</span>}
            </div>
          </div>
          {currentTier ? (
            <button
              onClick={() => { void handleRemoveTier(selectedUser._id); }}
              className="px-3 py-1.5 text-xs bg-red-500/15 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors font-heading font-semibold shrink-0"
            >
              إلغاء المستوى
            </button>
          ) : null}
        </div>

        {/* tier assignment buttons */}
        <div className="flex gap-2 mb-5">
          {TIERS.map((t) => {
            const isActive = currentTier === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { if (!isActive) void handleAssignTier(selectedUser._id, t.key); }}
                disabled={isAssigningTier || isActive}
                className={`flex-1 py-2 text-sm font-heading font-semibold rounded-lg transition-all ${
                  isActive
                    ? 'bg-brand-accent/20 text-brand-accent border border-brand-accent/40'
                    : 'bg-neutral-dark/50 text-neutral-medium hover:bg-neutral-dark hover:text-neutral-light border border-transparent'
                } disabled:opacity-60`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* service tabs */}
          <div className="flex gap-1 mb-5 p-0.5 bg-neutral-dark/50 rounded-lg">
            {SERVICE_TABS.map((tab) => {
            const colors = tab.type === 'temporaryNumbers' ? 'text-blue-400'
              : tab.type === 'temporaryNumbersReuse' ? 'text-cyan-400'
              : tab.type === 'voice' ? 'text-green-400'
              : tab.type === 'permanentNumbers' ? 'text-purple-400'
              : 'text-orange-400';
            const bgDot = tab.type === 'temporaryNumbers' ? 'bg-blue-500/40'
              : tab.type === 'temporaryNumbersReuse' ? 'bg-cyan-500/40'
              : tab.type === 'voice' ? 'bg-green-500/40'
              : tab.type === 'permanentNumbers' ? 'bg-purple-500/40'
              : 'bg-orange-500/40';
            return (
              <button
                key={tab.type}
                onClick={() => { setServiceTab(tab.type); setEditingCell(null); }}
                className={`flex items-center gap-2 flex-1 py-2.5 px-3 text-sm font-heading font-semibold rounded-md transition-all ${
                  serviceTab === tab.type
                    ? `bg-neutral-dark shadow-sm ${colors}`
                    : 'text-neutral-medium hover:text-neutral-light'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${serviceTab === tab.type ? bgDot : 'bg-neutral-medium/40'}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* server cards */}
        <div className="space-y-3">
          {SERVICE_TABS.find((t) => t.type === serviceTab)?.servers.map((sid) => {
            const margin = getServerMargin(margins, serviceTab, sid);
            const isEditing = editingCell?.type === serviceTab && editingCell?.serverId === sid;
            const label = SERVER_LABELS[serviceTab]?.[sid] ?? '';
            const pct = margin !== null ? Math.round(margin * 100) : 0;

            return (
              <div
                key={`${serviceTab}-${sid}`}
                className={`rounded-xl border transition-all duration-200 ${
                  isEditing
                    ? 'border-brand-accent bg-brand-accent/10 shadow-sm shadow-brand-accent/10'
                    : 'border-white/10 bg-neutral-dark/30 hover:border-brand-accent/30'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2 h-2 rounded-full ${margin !== null ? 'bg-green-400 shadow-sm shadow-green-400/40' : 'bg-neutral-medium/40'}`} />
                      <div>
                        <h4 className="font-heading font-semibold text-sm text-neutral-light">{label}</h4>
                        <p className="text-[10px] text-neutral-medium font-body">سيرفر {sid}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-heading font-bold font-mono tabular-nums ${
                      margin !== null ? 'text-brand-accent' : 'text-neutral-medium'
                    }`}>
                      {formatMargin(margin)}
                    </span>
                  </div>

                  {margin !== null && (
                    <div className="mb-3 h-1.5 bg-neutral-dark rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-brand-accent/60 to-brand-accent rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  )}

                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="نسبة الهامش"
                        className="flex-1 px-3 py-2 bg-neutral-dark border border-brand-accent/50 rounded-lg text-neutral-light text-sm focus:outline-none focus:border-brand-accent font-body"
                        autoFocus
                        min="-100"
                        step="0.01"
                      />
                      <span className="text-xs text-neutral-medium font-body">%</span>
                      <button
                        onClick={() => { void handleSaveMargin(); }}
                        disabled={isUpdating}
                        className="px-4 py-2 bg-green-500/90 text-white text-sm font-heading font-semibold rounded-lg hover:bg-green-500 disabled:opacity-50 transition-colors flex items-center gap-1"
                      >
                        {isUpdating ? <ButtonSpinner /> : 'حفظ'}
                      </button>
                      <button
                        onClick={() => setEditingCell(null)}
                        className="px-3 py-2 text-sm text-neutral-medium font-body rounded-lg hover:text-neutral-light transition-colors"
                      >
                        إلغاء
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleStartEdit(serviceTab, sid, margin)}
                        className="flex-1 py-2 px-3 text-sm font-heading font-semibold rounded-lg transition-colors margin-edit-btn"
                      >
                        {margin !== null ? 'تعديل' : 'تعيين هامش'}
                      </button>
                      {margin !== null && (
                        <button
                          onClick={() => setResetConfirm({ type: serviceTab, serverId: sid })}
                          className="px-3 py-2 bg-red-500/15 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          title="إزالة الهامش"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <ConfirmDialog
          isOpen={resetConfirm !== null}
          onClose={() => setResetConfirm(null)}
          onConfirm={() => { void handleResetMargin(); }}
          title="إعادة تعيين الهامش"
          message="سيتم إزالة الهامش المخصص لهذا المستخدم على هذا السيرفر، وسيعود لاستخدام الهامش العام أو هامش المستوى."
          confirmText="تأكيد الإزالة"
          cancelText="إلغاء"
          type="warning"
          isLoading={isUpdating}
        />
      </>
    );
  }

  // ========== OUTPUT ==========
  return (
    <>
      <style>{`
        .margin-edit-btn {
          background: rgba(168, 224, 224, 0.12);
          color: #A8E0E0;
          border: 1px solid rgba(168, 224, 224, 0.3);
        }
        .margin-edit-btn:hover {
          background: rgba(168, 224, 224, 0.22);
        }
        [data-theme="light"] .margin-edit-btn {
          background: rgba(168, 224, 224, 0.9) !important;
          color: #0E4A4A !important;
          border: none !important;
        }
        [data-theme="light"] .margin-edit-btn:hover {
          background: #A8E0E0 !important;
        }
      `}</style>
    <div className="min-h-screen animate-slide-up">
      {/* ===== HERO ===== */}
      <div className="mb-8 text-center animate-slide-up">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
          هوامش مخصصة
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          تعيين هامش ربح خاص لكل مستخدم على كل سيرفر
        </p>
      </div>

      {/* ===== TIER CONFIG BUTTON ===== */}
      <div className="flex justify-center mb-6">
        <button
          onClick={() => setShowTierConfig(!showTierConfig)}
          className="px-8 py-3 bg-gradient-to-r from-amber-600/80 to-yellow-600/80 text-white font-heading font-semibold rounded-xl hover:from-amber-600 hover:to-yellow-600 transition-all shadow-lg shadow-amber-600/20 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
          تعيين المستويات
        </button>
      </div>

      {/* ===== TIER CONFIG PANEL ===== */}
      {showTierConfig && (
        <div className="card-glass rounded-2xl p-5 md:p-6 mb-6 animate-scale-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-heading font-bold text-brand-accent">
              إعدادات مستويات الهوامش
            </h2>
            <button
              onClick={() => setShowTierConfig(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-neutral-dark hover:bg-neutral-dark/80 text-neutral-light transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {renderTierConfig()}
        </div>
      )}

      {/* ===== STATS ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'إجمالي المستخدمين', value: stats.total, color: 'text-brand-accent' },
          { label: 'أرقام مؤقتة', value: stats.temp, color: 'text-blue-400' },
          { label: 'إعادة استخدام', value: stats.reuse, color: 'text-cyan-400' },
          { label: 'اتصال', value: stats.voice, color: 'text-green-400' },
          { label: 'أرقام دائمة', value: stats.perm, color: 'text-purple-400' },
          { label: 'بروكسيات', value: stats.proxy, color: 'text-orange-400' },
        ].map((s) => (
          <div key={s.label} className="card-glass rounded-xl p-4 text-center animate-scale-in">
            <p className={`text-2xl md:text-3xl font-heading font-bold ${s.color} tabular-nums`}>
              {isAllLoading ? (
                <span className="inline-block w-8 h-5 bg-neutral-dark rounded animate-pulse" />
              ) : (
                s.value
              )}
            </p>
            <p className="text-xs text-neutral-medium font-body mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ===== SEARCH ===== */}
      <div className="card-glass rounded-2xl p-5 md:p-6 mb-6">
        <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث بايدي تيليغرام، البريد..."
              className="w-full pr-10 pl-4 py-3 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light focus:outline-none focus:border-brand-accent transition-colors font-body text-lg"
            />
            {isSearching && (
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <div className="w-5 h-5 border-2 border-brand-accent/30 border-t-brand-accent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={isSearching}
            className="w-full sm:w-auto px-8 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {isSearching ? <ButtonSpinner /> : 'بحث'}
          </button>
        </form>

        {searchResults && searchResults.length > 0 && (
          <div className="mt-5 space-y-2">
            <p className="text-sm font-heading font-semibold text-brand-accent">
              نتائج البحث ({searchResults.length})
            </p>
            {searchResults.map((user: UserMarginSearchResult) => {
              const userTierInfo = currentMargins?.assignedTier
                ? TIERS.find((t) => t.key === currentMargins.assignedTier)
                : undefined;
              return (
                <div key={user._id} className="flex items-center gap-2">
                  <button
                    onClick={() => handleSelectUser(user._id)}
                    className={`flex-1 text-right p-3 rounded-lg border transition-colors ${
                      selectedUserId === user._id && !modalUserId
                        ? 'bg-brand-accent/20 border-brand-accent'
                        : 'bg-neutral-dark border-neutral-dark hover:border-brand-accent/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-brand-accent/20 rounded-full flex items-center justify-center text-sm font-heading font-bold text-brand-accent">
                        {(user.firstName?.[0] || user.email?.[0] || '?').toUpperCase()}
                      </div>
                      <div className="text-right">
                        <p className="font-heading font-semibold text-sm text-neutral-light">
                          {user.firstName} {user.lastName || ''}
                        </p>
                        <p className="text-xs text-neutral-medium font-body">
                          {user.email && <span>{user.email}</span>}
                          {user.telegramId && <span> | ID: {user.telegramId}</span>}
                        </p>
                      </div>
                    </div>
                  </button>
                  {TIERS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => void handleAssignTier(user._id, t.key)}
                      disabled={isAssigningTier}
                      className={`px-2.5 py-1.5 text-[11px] font-heading font-semibold rounded-lg transition-all border ${
                        currentMargins?.assignedTier === t.key
                          ? `${t.color} bg-white/10 border-white/20`
                          : 'text-neutral-medium border-transparent hover:bg-neutral-dark hover:text-neutral-light'
                      } disabled:opacity-50`}
                      title={`تعيين ${t.label}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {searchResults && searchResults.length === 0 && (
          <div className="mt-5 text-center py-6 border-t border-neutral-dark/50">
            <p className="text-neutral-medium font-body">لا يوجد مستخدمين بهذه البيانات</p>
          </div>
        )}

        {!searchData && (
          <div className="mt-5 text-center py-6 border-t border-neutral-dark/50">
            <p className="text-neutral-medium/60 font-body text-sm">اكتب للبحث عن مستخدم...</p>
          </div>
        )}
      </div>

      {/* ===== USERS WITH MARGINS ===== */}
      <div className="card-glass rounded-2xl p-5 md:p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-heading font-bold text-brand-accent">
              المستخدمون ذوو الهوامش المخصصة
            </h2>
            <p className="text-sm text-neutral-medium font-body">اضغط على مستخدم لعرض وتعديل هوامشه</p>
          </div>
          <span className="text-sm px-3 py-1.5 bg-brand-accent/20 text-brand-accent rounded-full font-heading font-semibold">
            {isAllLoading ? '...' : `${allMargins.length} مستخدم`}
          </span>
        </div>

        {/* filter pills */}
        <div className="flex flex-wrap gap-1 mb-5 p-0.5 bg-neutral-dark/50 rounded-lg w-fit">
          {([
            { key: 'all' as FilterTab, label: 'الكل' },
            { key: 'temporaryNumbers' as FilterTab, label: 'مؤقتة' },
            { key: 'voice' as FilterTab, label: 'اتصال' },
            { key: 'permanentNumbers' as FilterTab, label: 'دائمة' },
            { key: 'proxies' as FilterTab, label: 'بروكسيات' },
          ]).map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterTab(f.key)}
              className={`px-4 py-1.5 text-xs font-heading font-semibold rounded-md transition-all ${
                filterTab === f.key
                  ? 'bg-brand-accent text-brand-primary shadow-sm'
                  : 'text-neutral-medium hover:text-neutral-light'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="w-[1px] mx-1 self-stretch bg-neutral-600/40" />
          {TIERS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilterTab(t.key)}
              className={`px-4 py-1.5 text-xs font-heading font-semibold rounded-md transition-all ${
                filterTab === t.key
                  ? `${t.color} bg-white/10 shadow-sm`
                  : 'text-neutral-medium hover:text-neutral-light'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isAllLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-neutral-dark/60 rounded-xl p-4 animate-pulse space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-neutral-dark" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-28 bg-neutral-dark rounded" />
                    <div className="h-2.5 w-40 bg-neutral-dark/60 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredMargins.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-neutral-dark rounded-xl">
            <svg className="w-12 h-12 mx-auto text-neutral-medium/30 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-neutral-medium/60 font-body">لا يوجد مستخدمون بهوامش مخصصة حتى الآن</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedMargins.map(renderUserRow)}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2" dir="ltr">
                <button
                  onClick={() => setMarginsPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1.5 text-sm bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-heading"
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setMarginsPage(p)}
                    className={`w-8 h-8 text-sm rounded-lg font-heading font-semibold transition-colors ${
                      p === safePage
                        ? 'bg-brand-accent text-brand-primary'
                        : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setMarginsPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-3 py-1.5 text-sm bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-heading"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ===== MODAL ===== */}
      {modalUserId && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
        >
          <div
            className="relative w-full sm:max-w-2xl min-h-screen sm:min-h-0 sm:rounded-2xl sm:my-8 bg-brand-primary shadow-2xl animate-slide-up p-0 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* modal header */}
            <div className="sticky top-0 z-10 bg-brand-primary/95 backdrop-blur-sm border-b border-neutral-dark/50 sm:border-none sm:relative sm:bg-transparent sm:backdrop-blur-none flex items-center justify-between p-4 sm:p-0 sm:mb-4">
              <h2 className="text-lg sm:text-xl font-heading font-bold text-brand-accent">
                هوامش المستخدم
              </h2>
              <button
                onClick={handleCloseModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-neutral-dark hover:bg-neutral-dark/80 text-neutral-light transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* modal body */}
            <div className="px-4 pb-8 sm:px-0 sm:pb-4">
              {renderModalContent()}
            </div>
          </div>
        </div>
      )}

      {/* ===== INLINE DETAIL (non-modal) ===== */}
      {selectedUserId && !modalUserId && (
        <div className="card-glass rounded-2xl p-5 md:p-6 mt-6 animate-scale-in">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-3 border-brand-accent/30 border-t-brand-accent rounded-full animate-spin mb-4" />
              <p className="text-neutral-medium font-body">جاري التحميل...</p>
            </div>
    ) : selectedUser ? (
      <>
        {renderModalContent()}

        <ConfirmDialog
          isOpen={resetConfirm !== null}
          onClose={() => setResetConfirm(null)}
          onConfirm={() => { void handleResetMargin(); }}
          title="إعادة تعيين الهامش"
          message="سيتم إزالة الهامش المخصص لهذا المستخدم على هذا السيرفر، وسيعود لاستخدام الهامش العام أو هامش المستوى."
          confirmText="تأكيد الإزالة"
          cancelText="إلغاء"
          type="warning"
          isLoading={isUpdating}
        />
      </>
    ) : null}
        </div>
      )}
      {/* ===== TIER CONFIRM DIALOG ===== */}
      <ConfirmDialog
        isOpen={tierConfirm !== null}
        onClose={() => setTierConfirm(null)}
        onConfirm={() => { void handleConfirmAssignTier(); }}
        title="تأكيد تعيين المستوى"
        message={tierConfirm ? `سيتم تعيين المستوى "${tierConfirm.label}" للمستخدم وتطبيق هوامشه على جميع السيرفرات. هل أنت متأكد؟` : ''}
        confirmText="تأكيد"
        cancelText="إلغاء"
        type="info"
        isLoading={isAssigningTier}
      />
    </div>
    </>
  );
}
